import React, { useState, useMemo } from 'react';
import { Table, Pagination, Input, InputGroup } from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';

import { MdCheckCircleOutline, MdErrorOutline } from 'react-icons/md';

import { Badge } from './TremorCompat';
import { COLOURS } from '../styles';

import { sheaterFaults } from '../constants';
import { getLimitOptions } from '../utils/pagination';
import { HEADER_STYLE, ELLIPSIS_STYLE, stripedRowClassName } from './tableStyles';
import { ColumnsMenu } from './ColumnsMenu';
import { useColumnWidths } from '../hooks/useColumnWidths';
import './dataTable.css';

const { Column, HeaderCell, Cell } = Table;

// Default column widths (px) for the resizable columns. The Faults column stays
// flexGrow so it fills leftover width (no right-edge gap); # is a fixed index
// column. See DevicesTable.js / useColumnWidths for the fixed-width + resizable
// rationale.
const DEFAULT_COLUMN_WIDTHS = {
  dateTime: 190,
  errorCode: 220,
  version: 130,
};

// Columns whose text a global filter scans (case-insensitive substring),
// reproducing the old Cloudscape TextFilter "search any column" behaviour.
const SEARCH_FIELDS = ['id', 'dateTimeDisplay', 'faults', 'errorCode', 'version'];

// Toggleable columns for the "Columns" show/hide picker (# is always shown),
// matching DevicesTable.js's show/hide pattern.
const COLUMN_META = [
  { id: 'dateTime', label: 'Date/Time' },
  { id: 'faults', label: 'Faults' },
  { id: 'errorCode', label: 'Error Code' },
  { id: 'version', label: 'Version' },
];
const DEFAULT_VISIBLE_COLUMNS = COLUMN_META.map((c) => c.id);
const COLUMN_PICKER_DATA = COLUMN_META.map((c) => ({ label: c.label, value: c.id }));

// Generic comparator: numeric when both sides are numbers, otherwise a
// numeric-aware locale compare (also sorts the ISO `dateTime` strings correctly).
function compareValues(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

const get_error_text_for_code = (code) => {
  var str = '';
  const none_string = ' - None';

  let value = parseInt(code);

  if (value === 0) {
    str = none_string;
  } else {
    let lookup = sheaterFaults.find((element) => element.value === value);
    if (lookup !== undefined) {
      str = ' - ' + lookup.label;
    }
  }

  return code === undefined || code === null || Number.isNaN(value) ? (
    <Badge icon={MdErrorOutline} color={COLOURS.warning}></Badge>
  ) : value === 0 || str === '' || str === none_string ? (
    <Badge icon={MdCheckCircleOutline} color={COLOURS.good}>
      {code + str}
    </Badge>
  ) : (
    <Badge icon={MdErrorOutline} color={COLOURS.warning}>
      {code + str}
    </Badge>
  );
};

export function FaultsDisplayTable(props) {
  const source = useMemo(() => props.formattedHistoryFaults ?? [], [props.formattedHistoryFaults]);

  const [filterText, setFilterText] = useState('');
  const [sortColumn, setSortColumn] = useState();
  const [sortType, setSortType] = useState();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const { colWidth, onColumnResize } = useColumnWidths(DEFAULT_COLUMN_WIDTHS);

  const isVisible = (id) => visibleColumns.includes(id);

  const query = filterText.trim().toLowerCase();

  const filtered = useMemo(
    () =>
      query
        ? source.filter((row) =>
            SEARCH_FIELDS.some((f) =>
              String(row[f] ?? '')
                .toLowerCase()
                .includes(query),
            ),
          )
        : source,
    [source, query],
  );

  const sorted = useMemo(() => {
    if (!sortColumn) return filtered;
    const arr = [...filtered].sort((a, b) => compareValues(a[sortColumn], b[sortColumn]));
    return sortType === 'desc' ? arr.reverse() : arr;
  }, [filtered, sortColumn, sortType]);

  // Page-size options adapt to the row count; the *selected* limit must be one
  // of the offered options, or rsuite's Pagination shows a blank "Select"
  // placeholder — if the current limit isn't offered (e.g. 50 selected but
  // only 12 rows), fall back to the largest option actually available.
  const limitOptions = getLimitOptions(sorted.length);
  const effectiveLimit = limitOptions.includes(limit) ? limit : limitOptions[limitOptions.length - 1];

  // page can be left pointing past the end once the row count shrinks (new
  // history loaded, a filter narrows results, ...) — clamp it rather than
  // rendering an empty page while rows exist earlier in the list.
  const maxPage = Math.max(1, Math.ceil(sorted.length / effectiveLimit));
  const effectivePage = Math.min(page, maxPage);

  const paged = useMemo(
    () => sorted.slice((effectivePage - 1) * effectiveLimit, effectivePage * effectiveLimit),
    [sorted, effectivePage, effectiveLimit],
  );

  const handleFilter = (value) => {
    setFilterText(value);
    setPage(1);
  };
  const handleSort = (column, type) => {
    setSortColumn(column);
    setSortType(type);
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <h4 style={{ margin: 0, flexShrink: 0 }}>
            Fault Records <span style={{ fontWeight: 'normal', color: '#8e8e93' }}>({source.length})</span>
          </h4>
          <InputGroup inside style={{ width: 220, minWidth: 160, flex: '1 1 160px' }}>
            <Input value={filterText} onChange={handleFilter} placeholder="Filter faults" aria-label="Filter faults" />
            <InputGroup.Addon>
              <SearchIcon />
            </InputGroup.Addon>
          </InputGroup>
        </div>
        <ColumnsMenu data={COLUMN_PICKER_DATA} value={visibleColumns} onChange={setVisibleColumns} />
      </div>

      <Table
        className="data-table"
        data={paged}
        autoHeight
        rowHeight={46}
        headerHeight={38}
        sortColumn={sortColumn}
        sortType={sortType}
        onSortColumn={handleSort}
        rowClassName={stripedRowClassName}
        renderEmpty={() => <div style={{ padding: 20, textAlign: 'center' }}>No Faults</div>}
      >
        {/* Fixed index column (not resizable). */}
        <Column width={70} align="center" verticalAlign="middle" sortable>
          <HeaderCell style={HEADER_STYLE}>#</HeaderCell>
          <Cell dataKey="id" style={{ fontSize: '0.9em' }} />
        </Column>

        {isVisible('dateTime') && (
          <Column width={colWidth('dateTime')} resizable onResize={onColumnResize} sortable>
            <HeaderCell style={HEADER_STYLE}>Date/Time</HeaderCell>
            <Cell dataKey="dateTime" verticalAlign="middle">
              {(rowData) => <span style={ELLIPSIS_STYLE}>{rowData.dateTimeDisplay}</span>}
            </Cell>
          </Column>
        )}

        {/* Faults stays flexGrow so it fills leftover width (no right-edge gap). */}
        {isVisible('faults') && (
          <Column flexGrow={1} minWidth={200} sortable>
            <HeaderCell style={HEADER_STYLE}>Faults</HeaderCell>
            <Cell dataKey="faults" verticalAlign="middle">
              {(rowData) => (
                <div className="tooltip">
                  {rowData.colour === COLOURS.good ? (
                    <Badge icon={MdCheckCircleOutline} color={COLOURS.good}>
                      {rowData.faults}
                    </Badge>
                  ) : (
                    <Badge icon={MdErrorOutline} color={rowData.colour}>
                      {rowData.faults}
                    </Badge>
                  )}
                </div>
              )}
            </Cell>
          </Column>
        )}

        {isVisible('errorCode') && (
          <Column width={colWidth('errorCode')} resizable onResize={onColumnResize} sortable>
            <HeaderCell style={HEADER_STYLE}>Error Code</HeaderCell>
            <Cell dataKey="errorCode" verticalAlign="middle">
              {(rowData) => <div className="tooltip">{get_error_text_for_code(rowData.errorCode)}</div>}
            </Cell>
          </Column>
        )}

        {isVisible('version') && (
          <Column width={colWidth('version')} resizable onResize={onColumnResize} sortable>
            <HeaderCell style={HEADER_STYLE}>Version</HeaderCell>
            <Cell dataKey="version" verticalAlign="middle">
              {(rowData) => <span style={ELLIPSIS_STYLE}>{rowData.version}</span>}
            </Cell>
          </Column>
        )}
      </Table>

      <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={7}
          size="sm"
          layout={['limit', '|', 'pager', 'skip']}
          total={sorted.length}
          limit={effectiveLimit}
          limitOptions={limitOptions}
          activePage={effectivePage}
          onChangePage={setPage}
          onChangeLimit={(l) => {
            setLimit(l);
            setPage(1);
          }}
        />
      </div>
    </div>
  );
}
